var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import BigNumber from 'bignumber.js';
import { getIcon } from './getIcon';
import { getSuiClient } from './client';
import { createAlphaFiSDK, validateNetwork } from './networkUtils';
import { webSocketService } from '../services/websocket';
// Constants
const MIN_SUI_BALANCE = 0.5;
const SUI_DECIMALS = 9;
const EXPLORER_BASE_URL = 'https://suiscan.xyz';
// Helper functions
const dispatchTransactionEvent = (txType, success) => {
    const eventType = success ? 'transactionSuccess' : 'transactionError';
    document.dispatchEvent(new CustomEvent(eventType, { detail: { txType, success } }));
};
const getNotificationTitle = (status, action) => {
    // Static titles
    if (status === 'awaiting')
        return 'Awaiting Response';
    if (status === 'insufficient_gas_balance')
        return 'Insufficient Gas Balance';
    // Action-based titles
    const titleMap = {
        success: {
            claim: 'Claim successful',
            deposit: 'Transaction Successful',
            withdraw: 'Transaction Successful',
            zap_deposit: 'Transaction Successful',
        },
        error: {
            deposit: 'Deposit Failed',
            withdraw: 'Withdraw Failed',
            claim: 'Claim Failed',
            zap_deposit: 'Transaction Failed',
        },
        cancelled: {
            deposit: 'Deposit Cancelled',
            withdraw: 'Withdraw Cancelled',
            claim: 'Claim Cancelled',
            zap_deposit: 'Transaction Cancelled',
        },
    };
    return titleMap[status][action];
};
const createExplorerLink = (network, digest) => {
    if (!digest)
        return '';
    const networkSuffix = network === 'testnet' ? 'testnet' : 'mainnet';
    return `${EXPLORER_BASE_URL}/${networkSuffix}/tx/${digest}`;
};
const formatPoolLocation = (action, poolName) => {
    if (!poolName)
        return '';
    const preposition = action === 'deposit' || action === 'zap_deposit' ? 'in' : 'from';
    return ` ${preposition} ${poolName} pool`;
};
const getNotificationDescription = (status, action, displayAmount, poolName, errorMessage) => {
    // Static messages
    if (status === 'awaiting')
        return 'Requesting wallet to approve transaction.';
    if (status === 'insufficient_gas_balance')
        return `At least ${MIN_SUI_BALANCE} SUI is required in your wallet to cover gas fees.`;
    // Action-based messages
    const poolLocation = formatPoolLocation(action, poolName);
    const descriptionMap = {
        success: {
            claim: `Successfully claimed ${displayAmount} rewards and staked them to the ALPHA Locked Vault.`,
            deposit: `You have successfully deposited ${displayAmount}${poolLocation}.`,
            withdraw: `You have successfully withdrawn ${displayAmount}${poolLocation}.`,
            zap_deposit: `You have successfully swapped and deposited ${displayAmount}${poolLocation}.`,
        },
        error: {
            claim: `Failed to claim rewards. ${errorMessage}`,
            deposit: `Failed to deposit ${displayAmount}${poolLocation}. ${errorMessage}`,
            withdraw: `Failed to withdraw ${displayAmount}${poolLocation}. ${errorMessage}`,
            zap_deposit: `Failed to swap and deposit ${displayAmount}${poolLocation}. ${errorMessage}`,
        },
        cancelled: {
            claim: 'Claim rewards transaction was cancelled.',
            deposit: `Deposit of ${displayAmount}${poolLocation} was cancelled.`,
            withdraw: `Withdrawal of ${displayAmount}${poolLocation} was cancelled.`,
            zap_deposit: `Zap deposit of ${displayAmount}${poolLocation} was cancelled.`,
        },
    };
    return descriptionMap[status][action];
};
const createNotificationDescription = (status, action, token, displayAmount, poolName, errorMessage) => {
    // Static statuses don't need any parameters
    if (status === 'awaiting' || status === 'insufficient_gas_balance') {
        return getNotificationDescription(status);
    }
    // Handle cases with action, token, and displayAmount
    if (action && token && displayAmount) {
        return getNotificationDescription(status, action, displayAmount, poolName, errorMessage);
    }
    // Fallback to default messages when no action/token/amount
    if (status === 'error')
        return errorMessage || '';
    return 'Transaction was cancelled.';
};
const createNotificationInfo = (network, status, params = {}) => {
    const { result, action, token, displayAmount, poolName } = params;
    const explorerLink = createExplorerLink(network, result === null || result === void 0 ? void 0 : result.digest);
    const notificationConfigs = {
        awaiting: {
            title: getNotificationTitle('awaiting'),
            description: createNotificationDescription('awaiting'),
            spinner: true,
        },
        success: {
            title: getNotificationTitle('success', action),
            description: createNotificationDescription('success', action, token, displayAmount, poolName),
            image: getIcon('success'),
            link: explorerLink,
        },
        error: {
            title: getNotificationTitle('error', action),
            description: action === 'deposit'
                ? `Failed to deposit ${displayAmount} in ${poolName} pool.`
                : action === 'withdraw'
                    ? `Failed to withdraw ${displayAmount} from ${poolName} pool.`
                    : action === 'zap_deposit'
                        ? `Failed to swap and deposit ${displayAmount} in ${poolName} pool.`
                        : `Failed to claim rewards.`,
            image: getIcon('failed'),
            link: explorerLink,
        },
        cancelled: {
            title: getNotificationTitle('cancelled', action),
            description: createNotificationDescription('cancelled', action, token, displayAmount, poolName),
            image: getIcon('failed'),
        },
        insufficient_gas_balance: {
            title: getNotificationTitle('insufficient_gas_balance'),
            description: createNotificationDescription('insufficient_gas_balance'),
            image: getIcon('failed'),
        },
    };
    return notificationConfigs[status];
};
export const transactionHandler = (params) => __awaiter(void 0, void 0, void 0, function* () {
    const { amount, poolId, poolName, network, action, modalState, address, signAndExecuteTransaction, setNotificationInfo, setIsNotificationOpen, setIsTransactionLoading, displayAmount, suiBalance, isAmountA, withdrawMax, resetInputValues, decimals, slippage, } = params;
    // Helper function to show notification and open modal
    const showNotification = (status, params = {}) => {
        setNotificationInfo(createNotificationInfo(network, status, params));
        setIsNotificationOpen(true);
    };
    // Helper function to handle error state
    const handleError = (errorMessage, shouldResetInputs = true, result) => {
        showNotification('error', {
            errorMessage,
            action,
            token: modalState.token,
            displayAmount,
            poolName,
            result,
        });
        setIsTransactionLoading(false);
        if (shouldResetInputs)
            resetInputValues === null || resetInputValues === void 0 ? void 0 : resetInputValues();
        dispatchTransactionEvent(action.toLowerCase(), false);
    };
    // Early validation
    if (!address || (!poolId && action !== 'claim')) {
        console.error('Address and Pool ID are required for transactions (except claim)');
        return;
    }
    // Convert raw SUI balance to decimal format
    const suiBalanceDecimal = new BigNumber(suiBalance).dividedBy(new BigNumber(10).pow(SUI_DECIMALS));
    if (suiBalanceDecimal.lt(MIN_SUI_BALANCE)) {
        showNotification('insufficient_gas_balance');
        return;
    }
    const alphafiSdk = createAlphaFiSDK(network, address);
    const validatedNetwork = validateNetwork(network);
    const suiClient = getSuiClient(validatedNetwork);
    // Show awaiting notification
    showNotification('awaiting');
    setIsTransactionLoading(true);
    try {
        // Use BigNumber to handle decimal calculations properly
        const amountBigNumber = new BigNumber(amount).multipliedBy(new BigNumber(10).pow(parseInt(decimals)));
        const amountBigInt = BigInt(amountBigNumber.toFixed(0)); // Convert to integer string first
        let transactionBlock;
        switch (action) {
            case 'deposit':
                if (!poolId)
                    throw new Error('Pool ID is required for deposit');
                transactionBlock = yield alphafiSdk.deposit({
                    poolId,
                    amount: amountBigInt,
                    isAmountA,
                });
                break;
            case 'withdraw':
                if (!poolId)
                    throw new Error('Pool ID is required for withdraw');
                transactionBlock = yield alphafiSdk.withdraw({
                    poolId,
                    amount: amountBigInt.toString(),
                    isAmountA,
                    withdrawMax: withdrawMax || false,
                });
                break;
            case 'claim':
                transactionBlock = yield alphafiSdk.claimAirdrop();
                break;
            case 'zap_deposit':
                if (poolId && isAmountA !== undefined) {
                    transactionBlock = yield alphafiSdk.zapDeposit({
                        poolId,
                        inputCoinAmount: amountBigInt,
                        isInputA: isAmountA,
                        address,
                        slippage: slippage ? slippage / 100 : 0.01,
                    });
                    transactionBlock === null || transactionBlock === void 0 ? void 0 : transactionBlock.setGasBudget(100000000); // 0.1 SUI
                }
                break;
            default:
                throw new Error(`Unsupported action: ${action}`);
        }
        if (!transactionBlock) {
            throw new Error('Failed to create transaction block');
        }
        signAndExecuteTransaction({ transaction: transactionBlock }, {
            onSuccess: (result) => __awaiter(void 0, void 0, void 0, function* () {
                var _a, _b, _c;
                try {
                    const txbCheck = yield suiClient.waitForTransaction({
                        digest: result.digest,
                        options: { showEffects: true },
                    });
                    if (((_b = (_a = txbCheck.effects) === null || _a === void 0 ? void 0 : _a.status) === null || _b === void 0 ? void 0 : _b.status) === 'success') {
                        showNotification('success', {
                            result,
                            action,
                            token: modalState.token,
                            displayAmount,
                            poolName,
                        });
                        resetInputValues === null || resetInputValues === void 0 ? void 0 : resetInputValues();
                        if (action === 'claim') {
                            webSocketService.sendClaimRewardsNotification(address);
                        }
                        else if (poolId) {
                            webSocketService.sendTransactionNotification(poolId, address);
                        }
                        dispatchTransactionEvent(action.toLowerCase(), true);
                    }
                    else {
                        const errorMessage = ((_c = txbCheck.effects) === null || _c === void 0 ? void 0 : _c.status.error) || 'Transaction failed';
                        console.error(`Error in ${action}:`, errorMessage);
                        handleError(errorMessage, true, result);
                    }
                }
                catch (error) {
                    console.error('Error waiting for transaction:', error);
                    handleError('Failed to confirm transaction', false, result);
                }
                finally {
                    setIsTransactionLoading(false);
                }
            }),
            onError: (error) => {
                console.error(`Error in ${action}:`, error);
                const message = error.message || 'Transaction cancelled';
                handleError(message);
            },
        });
    }
    catch (error) {
        const err = error;
        console.error(`Error in ${action}:`, err);
        handleError(err.message);
    }
});
